/**
 * ❓ In-App Help System — Contextual Help & Documentation
 *
 * Provides a help panel and contextual tooltips for product features.
 * Content is static (no external CMS needed for MVP).
 */

import React, { createContext, useCallback, useContext, useState } from 'react';
import { Book, ChevronRight, HelpCircle, Search, X } from 'lucide-react';

// ─── Help Content ─────────────────────────────────────────────

interface HelpArticle {
  id: string;
  title: string;
  category: string;
  content: string;
  keywords: string[];
}

const HELP_ARTICLES: HelpArticle[] = [
  {
    id: 'vsm-basics',
    title: 'Como criar um Mapa VSM',
    category: 'Canvas VSM',
    content: 'Use a barra de ferramentas (Ribbon) para adicionar atividades, decisões e inventários ao canvas. Clique e arraste para posicionar. Conecte os nós usando as alças de saída. Preencha métricas como tempo de processo, tempo de espera e FTR no painel lateral.',
    keywords: ['mapa', 'vsm', 'canvas', 'atividade', 'criar', 'processo'],
  },
  {
    id: 'workspace-modes',
    title: 'Modos ATUAL / KAIZEN / FUTURO',
    category: 'Canvas VSM',
    content: 'O Workflow Manager permite trabalhar em três realidades paralelas: ATUAL (baseline do processo), KAIZEN (oportunidades de melhoria identificadas) e FUTURO (estado projetado). Use a barra de modo abaixo do Ribbon para alternar. Dados de cada modo são isolados.',
    keywords: ['modo', 'atual', 'kaizen', 'futuro', 'workspace'],
  },
  {
    id: 'workflow-create',
    title: 'Criar um Processo',
    category: 'Processos',
    content: 'Acesse Operação → Gestão para criar templates de workflow. Defina etapas com SLA, WIP limits e estratégia de atribuição (manual, round robin ou balanceado por carga). Após criar o template, inicie instâncias pela página de Execução.',
    keywords: ['workflow', 'template', 'criar', 'sla', 'etapa'],
  },
  {
    id: 'workflow-sla',
    title: 'SLA e WIP Limits',
    category: 'Processos',
    content: 'Cada etapa pode ter SLA em horas (úteis ou corridas) e limite de WIP (trabalho em progresso). Quando o SLA está próximo do vencimento, um alerta amarelo aparece. Se ultrapassado, a tarefa fica vermelha. WIP limits impedem que mais tarefas sejam criadas.',
    keywords: ['sla', 'prazo', 'wip', 'limite', 'atraso'],
  },
  {
    id: 'action-plan',
    title: 'Plano de Ação 5W2H',
    category: 'Plano de Ação',
    content: 'Crie ações estruturadas respondendo: O quê (What), Por quê (Why), Quem (Who), Onde (Where), Quando (When), Como (How) e Quanto (How Much). Anexe evidências, defina prazos e acompanhe o percentual de conclusão.',
    keywords: ['ação', 'plano', '5w2h', 'prazo', 'evidência'],
  },
  {
    id: 'gains-monitor',
    title: 'Monitor de Ganhos / Kaizen',
    category: 'Ganhos',
    content: 'Registre iniciativas Kaizen com baseline (antes) e target (meta). O sistema monitora métricas periodicamente e detecta regressões automaticamente. O ROI é calculado comparando investimento vs retorno real. Métricas incluem Lead Time, Cycle Time, FTR e custo.',
    keywords: ['kaizen', 'ganho', 'roi', 'regressão', 'métrica', 'meta'],
  },
  {
    id: 'blueprint-governance',
    title: 'Modelos e Governança',
    category: 'Excelência',
    content: 'Modelos são padrões de processos. Crie a partir de mapas VSM ou processos, versione com semver (1.0.0), publique e promova a padrão corporativo. Apenas 1 modelo pode ser o padrão por categoria/organização.',
    keywords: ['modelo', 'governança', 'padrão', 'versão', 'publicar'],
  },
  {
    id: 'gamification',
    title: 'Sistema de Gamificação',
    category: 'Gamificação',
    content: 'Usuários acumulam pontos por ações completadas, Kaizens concluídos e metas de equipe. Existem 4 níveis: Bronze, Silver, Gold e Black Belt. Multiplicadores de prazo dobram os pontos para entregas antecipadas. Regressões geram penalidades Andon.',
    keywords: ['ponto', 'gamificação', 'ranking', 'nível', 'badge'],
  },
  {
    id: 'org-structure',
    title: 'Estrutura Organizacional',
    category: 'Organização',
    content: 'Configure a hierarquia da sua organização em Departamentos → Setores → Processos. Cada processo pode ter um responsável (owner). Essa estrutura é usada para contexto em workflows e relatórios.',
    keywords: ['departamento', 'setor', 'processo', 'organização', 'hierarquia'],
  },
  {
    id: 'billing',
    title: 'Planos e Faturamento',
    category: 'Conta',
    content: 'Gerencie seu plano em Organização → Faturamento. Planos disponíveis: Free (3 usuários), Starter (10 usuários), Pro (50 usuários) e Enterprise (ilimitado). Upgrades são proporcionais. O plano Free tem funcionalidades limitadas.',
    keywords: ['plano', 'billing', 'fatura', 'upgrade', 'preço'],
  },
  {
    id: 'export-data',
    title: 'Exportar Dados',
    category: 'Conta',
    content: 'Exporte mapas VSM como PDF, imagem ou A3. Dados de workflow, ações e Kaizen podem ser exportados em Excel/CSV. Para backup completo da organização (LGPD), acesse Organização → Backup e exporte tudo em JSON.',
    keywords: ['exportar', 'pdf', 'excel', 'csv', 'backup', 'download'],
  },
  {
    id: 'keyboard-shortcuts',
    title: 'Atalhos de Teclado',
    category: 'Geral',
    content: 'Ctrl+Z: Desfazer | Ctrl+Y: Refazer | Ctrl+S: Salvar | Ctrl+A: Selecionar tudo | Delete: Excluir selecionado | Ctrl+C/V: Copiar/Colar | Ctrl+G: Agrupar | Ctrl+F: Buscar no canvas',
    keywords: ['atalho', 'teclado', 'shortcut', 'ctrl'],
  },
];

// ─── Context ──────────────────────────────────────────────────

interface HelpContextType {
  isOpen: boolean;
  openHelp: (articleId?: string) => void;
  closeHelp: () => void;
  toggleHelp: () => void;
}

const HelpContext = createContext<HelpContextType>({
  isOpen: false,
  openHelp: () => {},
  closeHelp: () => {},
  toggleHelp: () => {},
});

export const useHelp = () => useContext(HelpContext);

// ─── Provider ─────────────────────────────────────────────────

export const HelpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialArticle, setInitialArticle] = useState<string | undefined>();

  const openHelp = useCallback((articleId?: string) => {
    setInitialArticle(articleId);
    setIsOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setIsOpen(false);
    setInitialArticle(undefined);
  }, []);

  const toggleHelp = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  return (
    <HelpContext.Provider value={{ isOpen, openHelp, closeHelp, toggleHelp }}>
      {children}
      {isOpen && <HelpPanel initialArticleId={initialArticle} onClose={closeHelp} />}
    </HelpContext.Provider>
  );
};

// ─── Help Panel ───────────────────────────────────────────────

const HelpPanel: React.FC<{ initialArticleId?: string; onClose: () => void }> = ({
  initialArticleId,
  onClose,
}) => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(initialArticleId || null);

  const filtered = search.trim()
    ? HELP_ARTICLES.filter(a => {
        const q = search.toLowerCase();
        return (
          a.title.toLowerCase().includes(q) ||
          a.content.toLowerCase().includes(q) ||
          a.keywords.some(k => k.includes(q))
        );
      })
    : HELP_ARTICLES;

  const categories = [...new Set(filtered.map(a => a.category))];
  const selected = selectedId ? HELP_ARTICLES.find(a => a.id === selectedId) : null;

  return (
    <div className="fixed inset-y-0 right-0 w-80 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-11 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="flex items-center gap-2">
          <Book className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold">Central de Ajuda</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
        <div className="relative">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSelectedId(null); }}
            placeholder="Buscar ajuda..."
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {selected ? (
          <div className="p-4">
            <button
              onClick={() => setSelectedId(null)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:underline mb-3"
            >
              ← Voltar
            </button>
            <span className="text-[10px] uppercase tracking-wider text-gray-400">{selected.category}</span>
            <h3 className="text-sm font-semibold mt-1 mb-3">{selected.title}</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
              {selected.content}
            </p>
          </div>
        ) : (
          <div className="py-2">
            {categories.map(cat => (
              <div key={cat}>
                <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                  {cat}
                </div>
                {filtered.filter(a => a.category === cat).map(article => (
                  <button
                    key={article.id}
                    onClick={() => setSelectedId(article.id)}
                    className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="text-xs text-gray-700 dark:text-gray-300">{article.title}</span>
                    <ChevronRight className="w-3 h-3 text-gray-400 shrink-0" />
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-4 py-8 text-center text-xs text-gray-400">
                Nenhum resultado para "{search}"
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 text-center">
        <a
          href="mailto:suporte@workflowbrasil.com"
          className="text-xs text-blue-600 hover:underline"
        >
          Precisa de mais ajuda? Contate o suporte
        </a>
      </div>
    </div>
  );
};

// ─── Help Button (for StatusBar or Header) ────────────────────

export const HelpButton: React.FC = () => {
  const { toggleHelp } = useHelp();

  return (
    <button
      onClick={toggleHelp}
      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
      title="Central de Ajuda (F1)"
    >
      <HelpCircle className="w-4 h-4 text-gray-500" />
    </button>
  );
};
